import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function CommissionOverview() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/overview`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch');
      const json = await res.json();
      setData(json);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  if (initialLoading || !data) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  const totals = data.totals || {};
  const statusCards = Array.isArray(data.status_cards) ? data.status_cards : [];

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>💰 Commission Management Overview</h2>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <SummaryCard label="Expected" value={fmt(totals.expected)} color="#3b82f6" />
        <SummaryCard label="Earned (Holding/Due)" value={fmt(totals.earned)} color="#f59e0b" />
        <SummaryCard label="Due (Remaining)" value={fmt(totals.due)} color="#ef4444" />
        <SummaryCard label="Total Paid" value={fmt(totals.paid)} color="#10b981" />
      </div>

      {/* System Counters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        <StateCard label="Active Beneficiaries" count={data.active_beneficiaries} color="#3b82f6" icon="👤" />
        <StateCard label="Active Agreements" count={data.active_agreements} color="#8b5cf6" icon="📄" />
        <StateCard label="Pending Approvals" count={data.pending_approvals} color="#f59e0b" icon="⏳" />
      </div>

      {/* Status Cards */}
      {statusCards.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>Entries by Status</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {statusCards.map(sc => (
              <div key={sc.status} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}><Badge value={sc.status} /></div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>{sc.count}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{fmt(sc.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* By Beneficiary */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>🏆 Top Beneficiaries</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>{['Beneficiary', 'Entries', 'Total'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {Array.isArray(data.by_beneficiary) && data.by_beneficiary.map(b => (
                  <tr key={b.id}>
                    <td style={tdStyle}>{b.display_name || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{b.entry_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(b.total)}</td>
                  </tr>
                ))}
                {(!data.by_beneficiary || data.by_beneficiary.length === 0) && (
                  <tr><td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Entries */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>🕐 Recent Entries</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>{['Entry #', 'Beneficiary', 'Amount', 'Status'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {Array.isArray(data.recent_entries) && data.recent_entries.map(e => (
                  <tr key={e.id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{e.entry_number}</td>
                    <td style={tdStyle}>{e.beneficiary?.display_name || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(e.final_amount)}</td>
                    <td style={tdStyle}><Badge value={e.status} /></td>
                  </tr>
                ))}
                {(!data.recent_entries || data.recent_entries.length === 0) && (
                  <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No entries yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{value}</div>
    </div>
  );
}

function StateCard({ label, count, color, icon }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, textAlign: 'center' }}>
      <div style={{ fontSize: 24, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color }}>{count || 0}</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>{label}</div>
    </div>
  );
}

export function Badge({ value }) {
  const colors = {
    expected: '#9ca3af', calculated: '#3b82f6', earned: '#8b5cf6', holding: '#f59e0b',
    due: '#ef4444', partially_paid: '#f97316', paid: '#10b981', pending: '#f59e0b',
    reversed: '#6b7280', cancelled: '#6b7280',
    draft: '#9ca3af', active: '#10b981', inactive: '#6b7280', archived: '#6b7280',
    suspended: '#ef4444', expired: '#ef4444', terminated: '#ef4444',
    pending_approval: '#f59e0b', approved: '#10b981', rejected: '#ef4444', applied: '#10b981',
    scheduled: '#3b82f6', reopened: '#8b5cf6', unpaid: '#ef4444',
  };
  const c = colors[value] || '#6b7280';
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: c + '18', color: c }}>
      {(value || '').replace(/_/g, ' ')}
    </span>
  );
}

export const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
export const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' };
export const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', whiteSpace: 'nowrap' };
export const inputStyle = { padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' };
export const btnPrimary = { padding: '8px 16px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
export const btnSecondary = { padding: '8px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
